'use client';

import { create } from 'zustand';

import type { PrV2LocalCommentModelState } from './types';

import { mapPendingCommentToPrV2Comment } from '../comment-read-model';
import {
  clearThreadResolvedOptimistic,
  linkCommentIdsInScope,
  pruneMissingPersistedComments,
  pruneMissingPersistedThreads,
  pruneMissingQueuedComments,
  removeByLookupIds,
  setThreadResolvedOptimistic,
  upsertCommentEntity,
  upsertReviewEntity,
  upsertThreadEntity,
} from './reconcile';
import {
  ensureScope,
  PR_V2_LOCAL_COMMENT_LIFECYCLES,
  PR_V2_LOCAL_THREAD_LIFECYCLES,
} from './types';
import { buildTimelineComment, buildTimelineReview } from './view';

export const usePrV2LocalCommentModelStore = create<PrV2LocalCommentModelState>((set) => ({
  scopes: {},
  syncPendingComments: (scopeKey, pendingComments) =>
    set((state) => {
      let scope = ensureScope(state.scopes[scopeKey]);
      for (const pendingComment of pendingComments) {
        scope = upsertCommentEntity(
          scope,
          mapPendingCommentToPrV2Comment(pendingComment),
          PR_V2_LOCAL_COMMENT_LIFECYCLES.queued,
        );
      }
      scope = pruneMissingQueuedComments(scope, pendingComments);
      return {
        scopes: {
          ...state.scopes,
          [scopeKey]: scope,
        },
      };
    }),
  reconcileComments: (scopeKey, comments) =>
    set((state) => {
      let scope = ensureScope(state.scopes[scopeKey]);
      for (const comment of comments) {
        scope = upsertCommentEntity(scope, comment);
      }
      scope = pruneMissingPersistedComments(scope, comments);
      return {
        scopes: {
          ...state.scopes,
          [scopeKey]: scope,
        },
      };
    }),
  reconcileReviews: (scopeKey, reviews) =>
    set((state) => {
      let scope = ensureScope(state.scopes[scopeKey]);
      for (const review of reviews) {
        scope = upsertReviewEntity(scope, review);
      }
      return {
        scopes: {
          ...state.scopes,
          [scopeKey]: scope,
        },
      };
    }),
  reconcileDiscussion: (scopeKey, timelineItems) =>
    set((state) => {
      let scope = ensureScope(state.scopes[scopeKey]);
      for (const timelineItem of timelineItems) {
        const comment = buildTimelineComment(timelineItem);
        if (comment) {
          scope = upsertCommentEntity(scope, comment);
        }

        const review = buildTimelineReview(timelineItem);
        if (review) {
          scope = upsertReviewEntity(scope, review);
        }
      }
      return {
        scopes: {
          ...state.scopes,
          [scopeKey]: scope,
        },
      };
    }),
  reconcileThreads: (scopeKey, reviewThreads) =>
    set((state) => {
      let scope = ensureScope(state.scopes[scopeKey]);
      for (const reviewThread of reviewThreads) {
        scope = upsertThreadEntity(scope, reviewThread);
      }
      scope = pruneMissingPersistedThreads(scope, reviewThreads);
      return {
        scopes: {
          ...state.scopes,
          [scopeKey]: scope,
        },
      };
    }),
  stageOptimisticComments: (scopeKey, comments) =>
    set((state) => {
      let scope = ensureScope(state.scopes[scopeKey]);
      for (const comment of comments) {
        scope = upsertCommentEntity(scope, comment, PR_V2_LOCAL_COMMENT_LIFECYCLES.submitting);
      }
      return {
        scopes: {
          ...state.scopes,
          [scopeKey]: scope,
        },
      };
    }),
  stageOptimisticReviews: (scopeKey, reviews) =>
    set((state) => {
      let scope = ensureScope(state.scopes[scopeKey]);
      for (const review of reviews) {
        scope = upsertReviewEntity(scope, review);
      }
      return {
        scopes: {
          ...state.scopes,
          [scopeKey]: scope,
        },
      };
    }),
  stageOptimisticThreads: (scopeKey, reviewThreads) =>
    set((state) => {
      let scope = ensureScope(state.scopes[scopeKey]);
      for (const reviewThread of reviewThreads) {
        scope = upsertThreadEntity(scope, reviewThread, PR_V2_LOCAL_THREAD_LIFECYCLES.submitting);
      }
      return {
        scopes: {
          ...state.scopes,
          [scopeKey]: scope,
        },
      };
    }),
  stageThreadResolutionOptimistic: (scopeKey, reviewThread, resolved) =>
    set((state) => {
      const scope = ensureScope(state.scopes[scopeKey]);
      return {
        scopes: {
          ...state.scopes,
          [scopeKey]: setThreadResolvedOptimistic(scope, reviewThread, resolved),
        },
      };
    }),
  clearThreadResolutionOptimistic: (scopeKey, lookupIds) =>
    set((state) => {
      const scope = state.scopes[scopeKey];
      if (!scope) {
        return state;
      }

      return {
        scopes: {
          ...state.scopes,
          [scopeKey]: clearThreadResolvedOptimistic(scope, lookupIds),
        },
      };
    }),
  linkCommentIds: (scopeKey, fromId, toId) =>
    set((state) => {
      const scope = state.scopes[scopeKey];
      if (!scope) {
        return state;
      }

      return {
        scopes: {
          ...state.scopes,
          [scopeKey]: linkCommentIdsInScope(scope, fromId, toId),
        },
      };
    }),
  removeCommentsByLookupIds: (scopeKey, lookupIds) =>
    set((state) => {
      const scope = state.scopes[scopeKey];
      if (!scope) {
        return state;
      }

      const next = removeByLookupIds(
        scope.commentsByClientId,
        scope.commentAliasToClientId,
        scope.commentOrder,
        lookupIds,
      );

      return {
        scopes: {
          ...state.scopes,
          [scopeKey]: {
            ...scope,
            commentsByClientId: next.items,
            commentAliasToClientId: next.aliasToClientId,
            commentOrder: next.order,
          },
        },
      };
    }),
  removeReviewsByLookupIds: (scopeKey, lookupIds) =>
    set((state) => {
      const scope = state.scopes[scopeKey];
      if (!scope) {
        return state;
      }

      const next = removeByLookupIds(
        scope.reviewsByClientId,
        scope.reviewAliasToClientId,
        scope.reviewOrder,
        lookupIds,
      );

      return {
        scopes: {
          ...state.scopes,
          [scopeKey]: {
            ...scope,
            reviewsByClientId: next.items,
            reviewAliasToClientId: next.aliasToClientId,
            reviewOrder: next.order,
          },
        },
      };
    }),
  removeThreadsByLookupIds: (scopeKey, lookupIds) =>
    set((state) => {
      const scope = state.scopes[scopeKey];
      if (!scope) {
        return state;
      }

      const next = removeByLookupIds(
        scope.threadsByClientId,
        scope.threadAliasToClientId,
        scope.threadOrder,
        lookupIds,
      );

      return {
        scopes: {
          ...state.scopes,
          [scopeKey]: {
            ...scope,
            threadsByClientId: next.items,
            threadAliasToClientId: next.aliasToClientId,
            threadOrder: next.order,
          },
        },
      };
    }),
}));
